import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  PlusIcon as Plus,
  FolderIcon as Folder,
  ArrowRightIcon as ArrowRight,
  TrashIcon as Trash,
  XIcon as X,
  SparkleIcon as Sparkle,
  EyeIcon as Eye,
  ArrowUpRightIcon as ArrowUpRight,
  FireIcon as Fire,
  DropIcon as Drop,
  SnowflakeIcon as Snowflake,
} from "@phosphor-icons/react";
import { listWorkspaces, createWorkspace, deleteWorkspace, fetchDeliverableTypes, getPipelineVelocity } from "@/lib/api";
import ActivityFeed from "@/components/workspace/ActivityFeed";
import WeeklyDigestCard from "@/components/digest/WeeklyDigestCard";
import SnapshotPulseWidget from "@/components/workspace/SnapshotPulseWidget";

function formatRelative(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const m = Math.round(diffMs / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

// Engagement-temperature signal used to colour the top-left glyph + tooltip.
// Hot:   opened TODAY                  → red square + Fire glyph
// Tepid: opened within the last 7 days → cyan square + Drop glyph
// Cold:  never opened                  → neutral square + Snowflake glyph
// Default (no engagement data at all, e.g. brand-new workspace) keeps the
// original blue Folder so first-time UX isn't a sea of snowflakes.
function temperatureFor(w) {
  if ((w.share_link_views_today || 0) > 0) {
    return {
      key: "hot",
      Icon: Fire,
      bg: "#fa4d56",
      label: `Hot — opened ${w.share_link_views_today} time${w.share_link_views_today === 1 ? "" : "s"} today`,
    };
  }
  if (w.last_share_link_viewed_at) {
    const ageDays = (Date.now() - new Date(w.last_share_link_viewed_at).getTime()) / 86400000;
    if (ageDays <= 7) {
      return {
        key: "tepid",
        Icon: Drop,
        bg: "#1192e8",
        label: `Tepid — opened ${formatRelative(w.last_share_link_viewed_at)}`,
      };
    }
    return {
      key: "cold",
      Icon: Snowflake,
      bg: "#8d8d8d",
      label: `Cold — last opened ${formatRelative(w.last_share_link_viewed_at)}`,
    };
  }
  // Never opened — keep the brand blue Folder so it looks neutral, not punitive.
  return {
    key: "neutral",
    Icon: Folder,
    bg: "var(--ml-primary)",
    label: "Not yet shared with the client",
  };
}

export default function Workspaces() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", client_name: "", description: "", seed_deliverable_type: "" });
  const [creating, setCreating] = useState(false);
  const [deliverableTypes, setDeliverableTypes] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const tempFilter = ["hot", "tepid", "cold", "neutral"].includes(searchParams.get("temp"))
    ? searchParams.get("temp")
    : null;
  const [velocity, setVelocity] = useState(null);

  const onSelectTemp = (key) => {
    const next = new URLSearchParams(searchParams);
    if (!key || tempFilter === key) {
      next.delete("temp");
    } else {
      next.set("temp", key);
    }
    setSearchParams(next, { replace: true });
  };

  const visibleList = tempFilter
    ? list.filter((w) => temperatureFor(w).key === tempFilter)
    : list;

  const refresh = useCallback(async () => {
    const l = await listWorkspaces();
    setList(l);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
    fetchDeliverableTypes().then(setDeliverableTypes);
    getPipelineVelocity(7).then(setVelocity);
  }, [refresh]);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error("Workspace name is required."); return; }
    setCreating(true);
    try {
      const payload = { ...form };
      if (!payload.seed_deliverable_type) delete payload.seed_deliverable_type;
      const ws = await createWorkspace(payload);
      setShowNew(false);
      setForm({ name: "", client_name: "", description: "", seed_deliverable_type: "" });
      navigate(`/workspaces/${ws.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create workspace.");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm("Delete this workspace? Sessions and deliverables are kept but unlinked.")) return;
    await deleteWorkspace(id);
    await refresh();
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 lg:py-16" data-testid="workspaces-page">
      <div className="mb-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
        <div className="md:col-span-8">
          <div className="ml-eyebrow mb-2">Client Workspaces</div>
          <h1 className="text-3xl lg:text-5xl font-semibold tracking-tight">
            Group sessions and deliverables
            <br />
            <span className="text-[var(--ml-text-secondary)]">by client engagement.</span>
          </h1>
          <p className="mt-5 text-[var(--ml-text-secondary)] max-w-2xl">
            One workspace per engagement. Hand a client a single shareable link to every
            conversation and deliverable produced for them.
          </p>
        </div>
        <div className="md:col-span-4 md:text-right">
          <button onClick={() => setShowNew(true)} className="ml-btn-primary" data-testid="new-workspace-btn">
            <Plus size={16} weight="bold" /> New Workspace
          </button>
        </div>
      </div>

      {loading ? (
        <div className="ml-card p-10 text-center text-[var(--ml-text-secondary)]">Loading workspaces…</div>
      ) : list.length === 0 ? (
        <div className="ml-card p-10 text-center" data-testid="empty-workspaces">
          <Folder size={28} color="#0f62fe" weight="duotone" className="mx-auto mb-3" />
          <div className="font-medium mb-1">No workspaces yet</div>
          <p className="text-sm text-[var(--ml-text-secondary)] mb-5">
            Create your first client engagement workspace.
          </p>
          <button onClick={() => setShowNew(true)} className="ml-btn-primary" data-testid="empty-new-workspace-btn">
            <Plus size={16} weight="bold" /> New Workspace
          </button>
        </div>
      ) : (
        <>
          <PipelineVelocity velocity={velocity} />
          <PipelinePulse list={list} active={tempFilter} onSelect={onSelectTemp} />
          <WeeklyDigestCard />
          <SnapshotPulseWidget />
          <ActivityFeed />
          {visibleList.length === 0 ? (
            <EmptyFilterState onClear={() => onSelectTemp(null)} active={tempFilter} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {visibleList.map((w) => {
            const temp = temperatureFor(w);
            const TempIcon = temp.Icon;
            return (
            <Link
              to={`/workspaces/${w.id}`}
              key={w.id}
              className="ml-card ml-card-interactive p-5 flex flex-col gap-3"
              data-testid={`workspace-card-${w.id}`}
            >
              <div className="flex items-start justify-between">
                <div
                  className="w-10 h-10 flex items-center justify-center"
                  style={{ backgroundColor: temp.bg }}
                  title={temp.label}
                  data-testid={`workspace-temp-${temp.key}-${w.id}`}
                >
                  <TempIcon size={20} weight="bold" color="#fff" />
                </div>
                <div className="flex items-center gap-1.5">
                  {w.share_link_view_count > 0 && (
                    <span
                      className="ml-mono text-[10px] uppercase tracking-widest px-2 py-1 bg-[var(--ml-success)]/10 text-[#0e6027] border border-[var(--ml-success)]/40 flex items-center gap-1.5"
                      title={
                        `Client opened the share link ${w.share_link_view_count} time${w.share_link_view_count === 1 ? "" : "s"}` +
                        (w.last_share_link_viewed_at ? ` · last ${formatRelative(w.last_share_link_viewed_at)}` : "") +
                        (w.share_link_views_today > 0 ? ` · ↑ +${w.share_link_views_today} today` : "")
                      }
                      data-testid={`client-opened-badge-${w.id}`}
                    >
                      <span
                        className="inline-block w-2 h-2 rounded-full bg-[#24a148] animate-pulse"
                        data-testid={`client-opened-dot-${w.id}`}
                      />
                      <Eye size={11} weight="bold" />
                      Client opened {w.share_link_view_count > 1 ? `· ${w.share_link_view_count}` : ""}
                      {w.share_link_views_today > 0 && (
                        <span
                          className="ml-1 normal-case lowercase text-[9px] text-[#0e6027]/80"
                          data-testid={`client-opened-today-${w.id}`}
                        >
                          ↑ +{w.share_link_views_today} today
                        </span>
                      )}
                    </span>
                  )}
                  {w.share_link_unfurl_platform_count > 0 && (
                    <span
                      className="ml-mono text-[10px] uppercase tracking-widest px-2 py-1 bg-[var(--ml-primary)]/8 text-[var(--ml-primary)] border border-[var(--ml-primary)]/30 flex items-center gap-1.5"
                      title={`Auto-unfurled by ${w.share_link_unfurl_platform_count} platform${w.share_link_unfurl_platform_count === 1 ? "" : "s"}: ${w.share_link_unfurl_platforms.join(", ")}`}
                      data-testid={`shared-platforms-badge-${w.id}`}
                    >
                      <ArrowUpRight size={11} weight="bold" />
                      Shared · {w.share_link_unfurl_platform_count}
                    </span>
                  )}
                  <button
                    onClick={(e) => handleDelete(w.id, e)}
                    className="ml-btn-ghost text-[var(--ml-error)]"
                    data-testid={`delete-workspace-${w.id}`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              </div>
              <div>
                <div className="text-lg font-semibold tracking-tight">{w.name}</div>
                {w.client_name && (
                  <div className="text-sm text-[var(--ml-text-secondary)] mt-0.5">{w.client_name}</div>
                )}
              </div>
              {w.description && (
                <p className="text-sm text-[var(--ml-text-primary)] leading-relaxed line-clamp-3">{w.description}</p>
              )}
              <div className="mt-auto pt-3 border-t border-[var(--ml-border)] flex items-center justify-between text-xs">
                <div className="flex gap-4 ml-mono uppercase tracking-widest text-[var(--ml-text-secondary)]">
                  <span>{w.session_count} sessions</span>
                  <span>{w.deliverable_count} deliv.</span>
                </div>
                <ArrowRight size={14} weight="bold" color="#0f62fe" />
              </div>
            </Link>
            );
          })}
            </div>
          )}
        </>
      )}

      {showNew && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => !creating && setShowNew(false)}
          data-testid="new-workspace-modal"
        >
          <div
            className="bg-white border border-[var(--ml-border)] max-w-lg w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="ml-eyebrow">New Workspace</div>
              <button onClick={() => setShowNew(false)} className="ml-btn-ghost" disabled={creating}>
                <X size={16} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1.5">
                  Workspace Name *
                </label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="ml-input"
                  placeholder="e.g. Acme — Order Mgmt Modernization"
                  data-testid="workspace-name-input"
                />
              </div>
              <div>
                <label className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1.5">
                  Client Name
                </label>
                <input
                  value={form.client_name}
                  onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))}
                  className="ml-input"
                  placeholder="e.g. Acme Distribution Inc."
                  data-testid="workspace-client-input"
                />
              </div>
              <div>
                <label className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1.5">
                  Description
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                  className="ml-textarea"
                  placeholder="Brief engagement context…"
                  data-testid="workspace-description-input"
                />
              </div>
              <div className="border-t border-[var(--ml-border)] pt-4">
                <label className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5 mb-1.5">
                  <Sparkle size={11} weight="bold" color="#0f62fe" />
                  Seed engagement <span className="lowercase text-[var(--ml-text-muted)]">(optional)</span>
                </label>
                <select
                  value={form.seed_deliverable_type}
                  onChange={(e) => setForm((f) => ({ ...f, seed_deliverable_type: e.target.value }))}
                  className="ml-input"
                  data-testid="workspace-seed-select"
                >
                  <option value="">— None —</option>
                  {deliverableTypes.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <p className="text-xs text-[var(--ml-text-secondary)] mt-1.5">
                  Auto-generate a kickoff deliverable from this workspace&apos;s metadata.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowNew(false)} className="ml-btn-secondary" disabled={creating}>
                Cancel
              </button>
              <button onClick={handleCreate} className="ml-btn-primary" disabled={creating} data-testid="create-workspace-btn">
                {creating ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Sparkline({ values, color = "#0f62fe", testid }) {
  // Inline SVG bar chart. Fixed 36×12 viewport, gap of 1 between bars, scaled
  // to the local max so it visually emphasises the distribution rather than
  // the absolute count (the number to the left already carries the magnitude).
  if (!values || values.length === 0) return null;
  const max = Math.max(...values, 1);
  const W = 36;
  const H = 12;
  const gap = 1;
  const barW = (W - gap * (values.length - 1)) / values.length;
  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      className="inline-block align-middle ml-1.5 shrink-0"
      data-testid={testid}
      role="img"
      aria-label={`Last ${values.length} days: ${values.join(", ")}`}
    >
      {values.map((v, i) => {
        const h = Math.max(v === 0 ? 0 : 1, (v / max) * H);
        const x = i * (barW + gap);
        const y = H - h;
        return (
          <rect
            key={`bar-${i}`}
            x={x}
            y={y}
            width={barW}
            height={h}
            fill={v === 0 ? "#e5e7eb" : color}
          />
        );
      })}
    </svg>
  );
}

/** Module-scoped link-styled stat shown on the velocity strip. Lifted out of
 *  PipelineVelocity to avoid the no-unstable-nested-components perf trap. */
function VelocityStat({ label, count, testid, daily, sparkTestid, metricKey }) {
  return (
    <Link
      to={`/telemetry?metric=${metricKey}&days=30`}
      className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] inline-flex items-center hover:text-[var(--ml-primary)] transition-colors cursor-pointer"
      data-testid={testid}
      title={`Open ${label} in the telemetry dashboard`}
    >
      <strong className="text-[var(--ml-text-primary)] font-semibold mr-1">{count}</strong>
      {label}
      {daily && daily.length > 0 && <Sparkline values={daily} testid={sparkTestid} />}
    </Link>
  );
}

function PipelineVelocity({ velocity }) {
  // Single-line "pipeline-velocity" strip rendered above the pulse. Hidden
  // while loading or when there's nothing useful to show. Three signals (the
  // funnel: demos → workspaces → client opens) sit side-by-side with the
  // mono uppercase typography used everywhere else in the listing header.
  if (!velocity) return null;
  const empty =
    !velocity.sample_pack_downloads &&
    !velocity.workspaces_created &&
    !velocity.client_share_opens;
  if (empty) return null;

  return (
    <div
      className="mb-3 flex flex-wrap items-center gap-4"
      data-testid="pipeline-velocity"
    >
      <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
        Velocity · last {velocity.window_days}d
      </span>
      <span className="text-[var(--ml-text-secondary)]">·</span>
      <VelocityStat
        label="sample pack downloads"
        count={velocity.sample_pack_downloads}
        testid="velocity-downloads"
        daily={velocity.sample_pack_downloads_daily}
        sparkTestid="velocity-downloads-sparkline"
        metricKey="downloads"
      />
      <span className="text-[var(--ml-text-secondary)]">→</span>
      <VelocityStat
        label="workspaces created"
        count={velocity.workspaces_created}
        testid="velocity-workspaces"
        daily={velocity.workspaces_created_daily}
        sparkTestid="velocity-workspaces-sparkline"
        metricKey="workspaces"
      />
      <span className="text-[var(--ml-text-secondary)]">→</span>
      <VelocityStat
        label="client opens"
        count={velocity.client_share_opens}
        testid="velocity-opens"
        daily={velocity.client_share_opens_daily}
        sparkTestid="velocity-opens-sparkline"
        metricKey="opens"
      />
    </div>
  );
}


/** Module-scoped pulse tag — receives `active` + `onSelect` as props so it
 *  stays a stable component type across re-renders of PipelinePulse. */
function PulseTag({ Icon, label, count, color, testid, filterKey, active, onSelect }) {
  if (count === 0) return null;
  const isActive = active === filterKey;
  const dimmed = active && !isActive;
  return (
    <button
      type="button"
      onClick={() => onSelect(filterKey)}
      data-testid={testid}
      data-active={isActive}
      className={`inline-flex items-center gap-1.5 px-2 py-1 border transition-colors cursor-pointer ${
        isActive
          ? "border-[var(--ml-text-primary)] bg-[var(--ml-surface)]"
          : "border-[var(--ml-border)] bg-white hover:border-[var(--ml-text-primary)]/40"
      } ${dimmed ? "opacity-50" : ""}`}
      title={isActive ? `Showing only ${label} — click to clear filter` : `Filter to ${label} workspaces`}
    >
      <span
        className="inline-flex items-center justify-center w-4 h-4 shrink-0"
        style={{ backgroundColor: color }}
      >
        <Icon size={10} weight="bold" color="#fff" />
      </span>
      <span className="ml-mono text-[11px] uppercase tracking-widest text-[var(--ml-text-primary)]">
        <strong className="font-semibold">{count}</strong> {label}
      </span>
    </button>
  );
}

function PipelinePulse({ list, active, onSelect }) {
  let hot = 0, tepid = 0, cold = 0, neutral = 0;
  for (const w of list) {
    const key = temperatureFor(w).key;
    if (key === "hot") hot += 1;
    else if (key === "tepid") tepid += 1;
    else if (key === "cold") cold += 1;
    else neutral += 1;
  }
  if (list.length === 0) return null;

  return (
    <div
      className="mb-5 flex flex-wrap items-center gap-2 text-sm"
      data-testid="pipeline-pulse"
    >
      <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mr-1">
        Pipeline pulse
      </span>
      <PulseTag Icon={Fire}      label="hot"            count={hot}     color="#fa4d56"           testid="pulse-hot"     filterKey="hot"     active={active} onSelect={onSelect} />
      <PulseTag Icon={Drop}      label="tepid"          count={tepid}   color="#1192e8"           testid="pulse-tepid"   filterKey="tepid"   active={active} onSelect={onSelect} />
      <PulseTag Icon={Snowflake} label="cold"           count={cold}    color="#8d8d8d"           testid="pulse-cold"    filterKey="cold"    active={active} onSelect={onSelect} />
      <PulseTag Icon={Folder}    label="not yet shared" count={neutral} color="var(--ml-primary)" testid="pulse-neutral" filterKey="neutral" active={active} onSelect={onSelect} />
      {active && (
        <button
          type="button"
          onClick={() => onSelect(null)}
          className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-primary)] hover:underline px-1"
          data-testid="pulse-clear-filter"
        >
          Clear filter ×
        </button>
      )}
      <span
        className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] ml-auto"
        data-testid="pulse-total"
      >
        Total {list.length}
      </span>
    </div>
  );
}

function EmptyFilterState({ onClear, active }) {
  return (
    <div className="ml-card p-10 text-center" data-testid="empty-filter-state">
      <div className="font-medium mb-1">No <span className="lowercase">{active}</span> workspaces right now</div>
      <p className="text-sm text-[var(--ml-text-secondary)] mb-5 max-w-md mx-auto">
        Nothing in this temperature bucket. Try a different filter or clear it to see everything.
      </p>
      <button onClick={onClear} className="ml-btn-primary" data-testid="empty-filter-clear">
        Clear filter
      </button>
    </div>
  );
}

